/**
 * Create / edit a site-wide announcement ribbon. Severity selection,
 * scheduling window, active toggle. Round-trips ISO-8601 datetimes via
 * scheduleUtils.
 */
import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { toLocalInput, fromLocalInput } from "./scheduleUtils";

export const AnnouncementEditModal = ({ initial, onClose, onSave }) => {
  const [message, setMessage] = useState(initial?.message || "");
  const [severity, setSeverity] = useState(initial?.severity || "info");
  const [launchAt, setLaunchAt] = useState(toLocalInput(initial?.launch_at));
  const [archiveAt, setArchiveAt] = useState(toLocalInput(initial?.archive_at));
  const [active, setActive] = useState(initial?.active !== false);

  const handleSave = () => {
    if (!message.trim()) { toast.error("Message must not be empty."); return; }
    if (launchAt && archiveAt && new Date(archiveAt) <= new Date(launchAt)) {
      toast.error("Archive date must be after launch date.");
      return;
    }
    onSave({
      message: message.trim(),
      severity,
      launch_at: fromLocalInput(launchAt),
      archive_at: fromLocalInput(archiveAt),
      active,
    });
  };

  return (
    <div
      data-testid="announcement-edit-modal"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="bg-white w-full max-w-lg border border-[#C5A059] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 bg-[#0A192F] text-white">
          <div className="font-accent text-xs tracking-wider">
            {initial ? "EDIT ANNOUNCEMENT" : "NEW ANNOUNCEMENT"}
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="ann-modal-close-btn">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[10px] font-accent text-slate-500 tracking-wider mb-1.5">MESSAGE</label>
            <textarea
              data-testid="ann-message-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              placeholder='e.g. "⚡ Flash sale — 40% off all bundles until Sunday midnight"'
              className="w-full text-sm p-2.5 border border-slate-300 focus:border-[#C5A059] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-[10px] font-accent text-slate-500 tracking-wider mb-1.5">SEVERITY</label>
            <div className="flex gap-1">
              {["info", "warning", "urgent"].map((s) => (
                <button
                  key={s}
                  data-testid={`ann-severity-${s}`}
                  onClick={() => setSeverity(s)}
                  className={`flex-1 text-[10px] font-accent tracking-wider py-2 px-3 ${
                    severity === s ? "bg-[#0A192F] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                >
                  {s.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-accent text-slate-500 tracking-wider mb-1.5">LAUNCH AT</label>
              <input
                data-testid="ann-launch-input"
                type="datetime-local"
                value={launchAt}
                onChange={(e) => setLaunchAt(e.target.value)}
                className="w-full text-sm p-2 border border-slate-300 focus:border-[#C5A059] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] font-accent text-slate-500 tracking-wider mb-1.5">ARCHIVE AT</label>
              <input
                data-testid="ann-archive-input"
                type="datetime-local"
                value={archiveAt}
                onChange={(e) => setArchiveAt(e.target.value)}
                className="w-full text-sm p-2 border border-slate-300 focus:border-[#C5A059] focus:outline-none"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              data-testid="ann-active-toggle"
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            Active (uncheck to disable without deleting)
          </label>

          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-xs font-accent text-slate-600 hover:text-[#0A192F] border border-slate-300">CANCEL</button>
            <button
              data-testid="ann-save-btn"
              onClick={handleSave}
              className="flex-1 px-4 py-2 text-xs font-accent text-white bg-[#0A192F] hover:bg-[#0A192F]/90"
            >
              {initial ? "SAVE CHANGES" : "CREATE ANNOUNCEMENT"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
